import React from 'react';
import styled from 'styled-components';
import { 
  Github, 
  Twitter, 
  MessageCircle, 
  Mail, 
  ArrowUp,
  Shield,
  Zap,
  Globe,
  Book
} from 'lucide-react';
import LOGO from '../assets/logo180.png';
import { useNavigate } from 'react-router-dom';

const FooterContainer = styled.footer`
  background: rgba(11, 20, 38, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4rem;
  position: relative;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem 1rem 2rem;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const LogoIcon = styled.img`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
`;

const FooterDescription = styled.p`
  color: #94A3B8;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  max-width: 300px;
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const FooterLink = styled.a`
  color: #94A3B8;
  text-decoration: none;
  font-weight: 400;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: #3B82F6;
    transform: translateX(4px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.3);
    color: #3B82F6;
    transform: translateY(-2px);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 2rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #3B82F6;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #94A3B8;
  font-size: 0.875rem;
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 0 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.div`
  color: #64748B;
  font-size: 0.875rem;
`;

const BackToTop = styled.button`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3B82F6;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
    transform: translateY(-2px);
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10B981;
  font-size: 0.875rem;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10B981;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const Footer = () => {

    const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const goToFaucet = () => {
    navigate("/testnetfaucet");
  }

  return (
    <FooterContainer>
      <FooterContent>
        <StatsContainer>
          <StatItem>
            <StatValue>99.99%</StatValue>
            <StatLabel>Network Uptime</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>2.0s</StatValue>
            <StatLabel>Block Time</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>0M</StatValue>
            <StatLabel>Total Transactions</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>4</StatValue>
            <StatLabel>Active Validators</StatLabel>
          </StatItem>
        </StatsContainer>

        <FooterGrid>
          <FooterSection>
            <FooterLogo>
              <LogoIcon src={LOGO} />
              Hydrixscan
            </FooterLogo>
            <FooterDescription>
              The most trusted blockchain explorer and analytics platform. 
              Explore transactions, addresses, and smart contracts with real-time data.
            </FooterDescription>
            <StatusIndicator>
              <StatusDot />
              All systems operational
            </StatusIndicator>
            <SocialLinks>
              <SocialLink href="#" aria-label="GitHub">
                <Github size={20} />
              </SocialLink>
              <SocialLink href="#" aria-label="Twitter">
                <Twitter size={20} />
              </SocialLink>
              <SocialLink href="#" aria-label="Discord">
                <MessageCircle size={20} />
              </SocialLink>
              <SocialLink href="#" aria-label="Email">
                <Mail size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Explorer</SectionTitle>
            <FooterLink href="#">
              <Shield size={16} />
              Transactions
            </FooterLink>
            <FooterLink href="#">
              <Globe size={16} />
              Blocks
            </FooterLink>
            <FooterLink href="#">
              <Zap size={16} />
              Validators
            </FooterLink>
            <FooterLink href="#">
              <Book size={16} />
              Smart Contracts
            </FooterLink>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Tools</SectionTitle>
            <FooterLink href="#">Gas Tracker</FooterLink>
            <FooterLink href="#">Node Tracker</FooterLink>
            <FooterLink onClick={goToFaucet}>Testnet Faucet</FooterLink>
            <FooterLink href="#">API Documentation</FooterLink>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Resources</SectionTitle>
            <FooterLink href="#">Knowledge Base</FooterLink>
            <FooterLink href="#">API Reference</FooterLink>
            <FooterLink href="#">Network Stats</FooterLink>
            <FooterLink href="#">Status Page</FooterLink>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            © 2024 Hydrixscan. All rights reserved.
          </Copyright>
          <BackToTop onClick={scrollToTop}>
            <ArrowUp size={16} />
            Back to top
          </BackToTop>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;